import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getFAICurrentInspection } from 'lib/selectors/getFAICurrentInspection';
import { getFAICurrentInspectionElapsedSince } from 'lib/selectors/getFAICurrentInspectionElapsedSince';
import { getFAIApproved } from 'lib/selectors/getFAIApproved';
import { getFAIRejected } from 'lib/selectors/getFAIRejected';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { formatFAIEvaluatorDisplayName } from 'lib/utils/faiEvaluatorDisplay';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { getAdjustedSetupDurationDates } from 'lib/selectors/getAdjustedSetupDurationDates';
import { getLatestSetupActivity } from 'lib/selectors/getLatestSetupActivity';
import { getIsPaused } from 'lib/selectors/getIsPaused';
import { FAI_STATUS } from 'lib/constants';
import { formatTime } from 'lib/utils/date';
import { formatExpectedSetupDurationForDisplay } from 'lib/utils/setupDurationDisplay';
import { Title } from 'pages/Machine/MachineMainWrapper/styled';
import {
  FullViewportOverlay,
  FaiMainContainer,
  FaiBannerBody,
  DetailsBlock,
  MetaLine,
} from './styled';
import { FAIEvaluatedBanner } from './FAIEvaluatedBanner';
import { FAISetupElapsedRow } from './FAISetupElapsedRow';
import { FAIWaitingTimer } from './FAIWaitingTimer';

const FAIBanner = () => {
  const { t } = useTranslation();
  const currentInspection = useSelector(getFAICurrentInspection);
  const isApproved = useSelector(getFAIApproved);
  const isRejected = useSelector(getFAIRejected);
  const operator = useSelector(getCurrentOperator);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const { adjustedSetupStartDate } = useSelector(getAdjustedSetupDurationDates);
  const latestSetupActivity = useSelector(getLatestSetupActivity);
  const isPaused = useSelector(getIsPaused);
  const faiElapsedSince = useSelector(getFAICurrentInspectionElapsedSince);

  if (!currentInspection) {
    return null;
  }

  const { requestedAt, startedAt, requester } = currentInspection;
  const requestedTimeStr = requestedAt ? formatTime(requestedAt) : '';
  const requesterName = requester?.name ?? operator?.name ?? '';

  const setupExpectedDisplay = formatExpectedSetupDurationForDisplay(
    latestActivitySet?.expectedSetupDuration
  );
  const showSetupLine = Boolean(
    latestActivitySet?.activitySetRef ||
      latestActivitySet?.workOrderOperationRef ||
      latestActivitySet?.expectedSetupDuration ||
      adjustedSetupStartDate
  );

  const setupElapsedProps = {
    showSetupLine,
    adjustedSetupStartDate,
    isPaused,
    latestSetupActivity,
    setupExpectedDisplay,
  };

  if (isRejected || isApproved) {
    return <FAIEvaluatedBanner />;
  }

  if (currentInspection.status === FAI_STATUS.IN_PROGRESS) {
    const startedTimeStr = startedAt ? formatTime(startedAt) : '';
    const evaluatorName = formatFAIEvaluatorDisplayName(currentInspection);

    return (
      <FullViewportOverlay data-testid="fai-banner">
        <FaiMainContainer className="fai-banner-in-progress">
          <FaiBannerBody>
            <Title>{t('fai.inspectionInProgress')}</Title>
            <DetailsBlock>
              {startedTimeStr && evaluatorName ? (
                <MetaLine>
                  {t('fai.inspectionStartedAtBy', {
                    time: startedTimeStr,
                    name: evaluatorName,
                  })}
                </MetaLine>
              ) : startedTimeStr ? (
                <MetaLine>
                  {t('fai.inspectionStartedAtTime', { time: startedTimeStr })}
                </MetaLine>
              ) : null}
              <FAISetupElapsedRow {...setupElapsedProps} />
            </DetailsBlock>
            <FAIWaitingTimer startDate={faiElapsedSince} />
          </FaiBannerBody>
        </FaiMainContainer>
      </FullViewportOverlay>
    );
  }

  if (currentInspection.status !== FAI_STATUS.PENDING) {
    return null;
  }

  return (
    <FullViewportOverlay data-testid="fai-banner">
      <FaiMainContainer className="fai-banner-pending">
        <FaiBannerBody>
          <Title>{t('fai.awaitingInspectionScreenTitle')}</Title>
          <DetailsBlock>
            {requestedTimeStr && requesterName ? (
              <MetaLine>
                {t('fai.requestedAtBy', {
                  time: requestedTimeStr,
                  name: requesterName,
                })}
              </MetaLine>
            ) : null}
            <FAISetupElapsedRow {...setupElapsedProps} />
          </DetailsBlock>
          <FAIWaitingTimer startDate={faiElapsedSince} />
        </FaiBannerBody>
      </FaiMainContainer>
    </FullViewportOverlay>
  );
};

export default FAIBanner;
